import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RecordSubscription } from "pocketbase";

import { pb, type CollectionName } from "./pb";
import type { CollectionResponses } from "./pb-types";
import { queryKeys } from "./queryKeys";

/**
 * Realtime subscriptions.
 *
 * PocketBase pushes create/update/delete events over SSE. Rather than have each
 * feature reimplement subscribe/unsubscribe bookkeeping, a feature calls
 * `useRealtimeCollection("tasks")` once and every query keyed with
 * `queryKeys.collection("tasks", ...)` is invalidated when the server changes.
 */

export type RealtimeAction = "create" | "update" | "delete";

export type RealtimeEvent<T> = RecordSubscription<T>;

export type UseRealtimeCollectionOptions<T> = {
  /** `"*"` for the whole collection (default) or a single record id. */
  topic?: string;
  /** Set false to hold off subscribing, e.g. before the user has signed in. */
  enabled?: boolean;
  /** Extra handling on top of the automatic cache invalidation. */
  onEvent?: (event: RealtimeEvent<T>) => void;
  /** Skip the automatic invalidation and handle the event yourself. */
  invalidate?: boolean;
};

/**
 * Subscribe to a collection for as long as the component is mounted.
 *
 * Safe under React StrictMode: the subscription created by a double-invoked
 * effect is torn down even if it resolves after the cleanup has run.
 */
export function useRealtimeCollection<C extends CollectionName>(
  collection: C,
  options: UseRealtimeCollectionOptions<CollectionResponses[C]> = {},
): void {
  const { topic = "*", enabled = true, onEvent, invalidate = true } = options;
  const queryClient = useQueryClient();

  // Kept in a ref so a new inline callback does not tear down the subscription.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => Promise<void>) | undefined;

    pb.collection(collection)
      .subscribe(topic, (event) => {
        if (invalidate) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.collection(collection) });
        }
        handlerRef.current?.(event);
      })
      .then((fn) => {
        if (cancelled) {
          void fn();
        } else {
          unsubscribe = fn;
        }
      })
      .catch((error: unknown) => {
        // A failed subscription degrades to refetch-on-focus rather than
        // breaking the screen, so it is reported and not thrown.
        console.warn(`[Kobo] realtime subscription to "${collection}" failed`, error);
      });

    return () => {
      cancelled = true;
      void unsubscribe?.();
    };
  }, [collection, topic, enabled, invalidate, queryClient]);
}

/**
 * Imperative equivalent of the hook, for code outside the React tree.
 * Resolves to the unsubscribe function.
 */
export function subscribeToCollection<C extends CollectionName>(
  collection: C,
  callback: (event: RealtimeEvent<CollectionResponses[C]>) => void,
  topic = "*",
): Promise<() => Promise<void>> {
  return pb.collection(collection).subscribe(topic, callback);
}
